import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { EVENT_TYPE_GROUPS, eventTypeKey, humanizeEventType } from '../event-type-label';
import type { AuditLogFilters } from '../use-audit-log';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

const SHADOW_THICK = sentinel.shadowThick; // shadowThick
const MOTION_FAST = '120ms';
const EASE_OUT = sentinel.easeOut;
const FONT_BODY =
  sentinel.fontBody;

const styles = stylex.create({
  card: {
    display: 'grid',
    gap: '1rem',
    padding: '1.125rem 1.25rem',
    marginBottom: '1.25rem',
    backgroundColor: sentinel.surfaceElevated,
    borderRadius: '0.75rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: '0.75rem 1rem',
  },
  // Event-type picker anchor; the popover is absolutely positioned to it.
  field: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3125rem',
    minWidth: '14rem',
  },
  fieldLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: sentinel.textSubtle,
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    width: '100%',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    paddingLeft: '0.75rem',
    paddingRight: '0.625rem',
    borderRadius: '0.5rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: { default: sentinel.borderEmphasis, ':hover': sentinel.accentAction },
    backgroundColor: sentinel.surfaceSunken,
    color: sentinel.textBody,
    fontSize: '0.875rem',
    fontFamily: FONT_BODY,
    cursor: 'pointer',
    textAlign: 'left',
    transitionProperty: 'border-color',
    transitionDuration: MOTION_FAST,
    transitionTimingFunction: EASE_OUT,
  },
  triggerOpen: { borderColor: sentinel.accentAction },
  triggerText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  triggerPlaceholder: { color: sentinel.textSubtle },
  caret: {
    color: sentinel.textSubtle,
    flexShrink: 0,
    transitionProperty: 'transform',
    transitionDuration: MOTION_FAST,
    transitionTimingFunction: EASE_OUT,
  },
  caretOpen: { transform: 'rotate(180deg)' },
  popover: {
    position: 'absolute',
    top: 'calc(100% + 0.375rem)',
    left: 0,
    zIndex: 20,
    width: 'min(22rem, 90vw)',
    maxHeight: '20rem',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: sentinel.surfaceOverlay,
    borderRadius: '0.625rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderEmphasis,
    boxShadow: SHADOW_THICK,
    overflow: 'hidden',
  },
  searchWrap: {
    padding: '0.625rem',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: sentinel.borderDefault,
  },
  searchInput: {
    width: '100%',
    paddingTop: '0.4375rem',
    paddingBottom: '0.4375rem',
    paddingLeft: '0.625rem',
    paddingRight: '0.625rem',
    borderRadius: '0.375rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: { default: sentinel.borderEmphasis, ':focus': sentinel.accentAction },
    backgroundColor: sentinel.surfaceSunken,
    color: sentinel.textBody,
    fontSize: '0.8125rem',
    fontFamily: FONT_BODY,
    outline: 'none',
  },
  optionList: {
    overflowY: 'auto',
    paddingTop: '0.375rem',
    paddingBottom: '0.375rem',
  },
  groupHeader: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: sentinel.textSubtle,
    paddingTop: '0.5rem',
    paddingBottom: '0.25rem',
    paddingLeft: '0.75rem',
    paddingRight: '0.75rem',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    paddingTop: '0.375rem',
    paddingBottom: '0.375rem',
    paddingLeft: '0.75rem',
    paddingRight: '0.75rem',
    fontSize: '0.8125rem',
    color: sentinel.textBody,
    cursor: 'pointer',
    backgroundColor: { default: 'transparent', ':hover': sentinel.stateHover },
  },
  checkbox: { accentColor: sentinel.accentAction, cursor: 'pointer' },
  noMatches: {
    padding: '1rem 0.75rem',
    fontSize: '0.8125rem',
    color: sentinel.textMuted,
    textAlign: 'center',
  },
  dateField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3125rem',
  },
  dateInput: {
    paddingTop: '0.4375rem',
    paddingBottom: '0.4375rem',
    paddingLeft: '0.625rem',
    paddingRight: '0.5rem',
    borderRadius: '0.5rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: { default: sentinel.borderEmphasis, ':focus': sentinel.accentAction },
    backgroundColor: sentinel.surfaceSunken,
    color: sentinel.textBody,
    fontSize: '0.8125rem',
    fontFamily: FONT_BODY,
    colorScheme: 'dark',
    outline: 'none',
  },
  clearButton: {
    marginLeft: 'auto',
    alignSelf: 'flex-end',
    paddingTop: '0.4375rem',
    paddingBottom: '0.4375rem',
    paddingLeft: '0.75rem',
    paddingRight: '0.75rem',
    borderRadius: '0.5rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: { default: sentinel.borderEmphasis, ':hover': sentinel.accentAction },
    backgroundColor: 'transparent',
    color: sentinel.textMuted,
    fontSize: '0.8125rem',
    fontFamily: FONT_BODY,
    cursor: 'pointer',
    transitionProperty: 'color, border-color',
    transitionDuration: MOTION_FAST,
    transitionTimingFunction: EASE_OUT,
    ':disabled': { opacity: 0.38, cursor: 'not-allowed' },
  },
  // Selected event types rendered as removable chips below the controls.
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.4375rem',
    alignItems: 'center',
  },
  chipsLabel: {
    fontSize: '0.75rem',
    color: sentinel.textSubtle,
    marginRight: '0.125rem',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    paddingTop: '0.1875rem',
    paddingBottom: '0.1875rem',
    paddingLeft: '0.625rem',
    paddingRight: '0.375rem',
    borderRadius: '999px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: `color-mix(in oklab, ${sentinel.accentAction} 32%, transparent)`,
    backgroundColor: `color-mix(in oklab, ${sentinel.accentAction} 12%, transparent)`,
    color: sentinel.textBody,
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  chipRemove: {
    display: 'grid',
    placeItems: 'center',
    width: '1rem',
    height: '1rem',
    borderRadius: '999px',
    border: 'none',
    padding: 0,
    backgroundColor: { default: 'transparent', ':hover': sentinel.stateHover },
    color: { default: sentinel.textMuted, ':hover': sentinel.textBody },
    cursor: 'pointer',
    flexShrink: 0,
  },
});

const CARET_PROPS = {
  width: 14,
  height: 14,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

interface Props {
  value: AuditLogFilters;
  onChange: (next: AuditLogFilters) => void;
}

export function AuditFilterBar({ value, onChange }: Props) {
  const { t, i18n } = useTranslation('audit-log');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);

  const label = (tag: string) =>
    i18n.exists(eventTypeKey(tag), { ns: 'audit-log' }) ? t(eventTypeKey(tag)) : humanizeEventType(tag);

  // Close the popover on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    function onPointer(e: PointerEvent) {
      if (fieldRef.current && !fieldRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return EVENT_TYPE_GROUPS;
    return EVENT_TYPE_GROUPS.map((g) => ({
      category: g.category,
      types: g.types.filter((tp) => label(tp).toLowerCase().includes(q)),
    })).filter((g) => g.types.length > 0);
    // i18n.language is the reactive dep that triggers re-filter when locale changes
  }, [search, i18n.language]);

  const toggle = (tag: string) => {
    const set = new Set(value.eventTypes);
    if (set.has(tag)) {
      set.delete(tag);
    } else {
      set.add(tag);
    }
    onChange({ ...value, eventTypes: [...set] });
  };

  const selectedCount = value.eventTypes.length;
  const hasFilters = selectedCount > 0 || value.dateFrom != null || value.dateTo != null;
  const triggerLabel =
    selectedCount === 0
      ? t('filters.all')
      : t('filters.selectedCount', { count: selectedCount });

  return (
    <div {...stylex.props(styles.card)}>
      <div {...stylex.props(styles.controls)}>
        <div {...stylex.props(styles.field)} ref={fieldRef}>
          <span {...stylex.props(styles.fieldLabel)} id="audit-events-label">
            {t('filters.events')}
          </span>
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-labelledby="audit-events-label"
            onClick={() => { setOpen((v) => !v); }}
            {...stylex.props(styles.trigger, open && styles.triggerOpen)}
          >
            <span
              {...stylex.props(
                styles.triggerText,
                selectedCount === 0 && styles.triggerPlaceholder,
              )}
            >
              {triggerLabel}
            </span>
            <span {...stylex.props(styles.caret, open && styles.caretOpen)}>
              <svg {...CARET_PROPS}>
                <path d="M4 6l4 4 4-4" />
              </svg>
            </span>
          </button>
          {open ? (
            <div {...stylex.props(styles.popover)} role="dialog" aria-label={t('filters.events')}>
              <div {...stylex.props(styles.searchWrap)}>
                <input
                  type="search"
                  aria-label={t('filters.search')}
                  placeholder={t('filters.search')}
                  value={search}
                  autoFocus
                  onChange={(e) => { setSearch(e.target.value); }}
                  {...stylex.props(styles.searchInput)}
                />
              </div>
              <div {...stylex.props(styles.optionList)}>
                {groups.length === 0 ? (
                  <p {...stylex.props(styles.noMatches)}>{t('filters.noMatches')}</p>
                ) : (
                  groups.map((g) => (
                    <div key={g.category}>
                      <p {...stylex.props(styles.groupHeader)}>{t(`categories.${g.category}`)}</p>
                      {g.types.map((tag) => (
                        <label key={tag} {...stylex.props(styles.option)}>
                          <input
                            type="checkbox"
                            checked={value.eventTypes.includes(tag)}
                            onChange={() => { toggle(tag); }}
                            {...stylex.props(styles.checkbox)}
                          />
                          {label(tag)}
                        </label>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : null}
        </div>

        <label {...stylex.props(styles.dateField)}>
          <span {...stylex.props(styles.fieldLabel)}>{t('filters.from')}</span>
          <input
            type="date"
            value={value.dateFrom?.slice(0, 10) ?? ''}
            onChange={(e) => {
              onChange({ ...value, dateFrom: e.target.value ? `${e.target.value}T00:00:00Z` : null });
            }}
            {...stylex.props(styles.dateInput)}
          />
        </label>
        <label {...stylex.props(styles.dateField)}>
          <span {...stylex.props(styles.fieldLabel)}>{t('filters.to')}</span>
          <input
            type="date"
            value={value.dateTo?.slice(0, 10) ?? ''}
            onChange={(e) => {
              onChange({ ...value, dateTo: e.target.value ? `${e.target.value}T23:59:59Z` : null });
            }}
            {...stylex.props(styles.dateInput)}
          />
        </label>

        <button
          type="button"
          disabled={!hasFilters}
          onClick={() => { onChange({ eventTypes: [], dateFrom: null, dateTo: null }); }}
          {...stylex.props(styles.clearButton)}
        >
          {t('filters.clear')}
        </button>
      </div>

      {selectedCount > 0 ? (
        <div {...stylex.props(styles.chips)} aria-label={t('filters.events')}>
          <span {...stylex.props(styles.chipsLabel)}>{t('filters.selected')}</span>
          {value.eventTypes.map((tag) => (
            <span key={tag} {...stylex.props(styles.chip)}>
              {label(tag)}
              <button
                type="button"
                aria-label={t('filters.remove', { label: label(tag) })}
                onClick={() => { toggle(tag); }}
                {...stylex.props(styles.chipRemove)}
              >
                <svg {...CARET_PROPS} width={11} height={11}>
                  <path d="M4 4l8 8M12 4l-8 8" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
